import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const DISMISS_KEY = "spectre.pwa.install.dismissedAt";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () => {
	if (typeof window === "undefined") return false;
	if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
	if (window.navigator && window.navigator.standalone) return true;
	return false;
};

const isCapacitor = () => {
	if (typeof window === "undefined") return false;
	if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) return true;
	const proto = window.location && window.location.protocol;
	return proto === "capacitor:" || proto === "ionic:";
};

const isIOS = () => {
	if (typeof window === "undefined") return false;
	const ua = window.navigator.userAgent || "";
	const iPadOS = ua.includes("Macintosh") && navigator.maxTouchPoints > 1;
	return /iPad|iPhone|iPod/.test(ua) || iPadOS;
};

const isIOSSafari = () => {
	if (!isIOS()) return false;
	const ua = window.navigator.userAgent || "";
	return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
};

const isDismissed = () => {
	try {
		const raw = window.localStorage.getItem(DISMISS_KEY);
		if (!raw) return false;
		const at = Number(raw);
		if (!Number.isFinite(at)) return false;
		return Date.now() - at < DISMISS_TTL_MS;
	} catch (_e) {
		return false;
	}
};

const markDismissed = () => {
	try {
		window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
	} catch (_e) {
		/* noop */
	}
};

export default function InstallBanner() {
	const { t } = useTranslation();
	const [deferred, setDeferred] = useState(null);
	const [visible, setVisible] = useState(false);
	const [iosHint, setIosHint] = useState(false);

	useEffect(() => {
		if (isCapacitor()) return undefined;
		if (isStandalone()) return undefined;
		if (isDismissed()) return undefined;

		const onPrompt = (e) => {
			e.preventDefault();
			setDeferred(e);
			setVisible(true);
		};
		const onInstalled = () => {
			setVisible(false);
			setDeferred(null);
			markDismissed();
		};

		window.addEventListener("beforeinstallprompt", onPrompt);
		window.addEventListener("appinstalled", onInstalled);

		if (isIOSSafari()) {
			setIosHint(true);
			setVisible(true);
		}

		return () => {
			window.removeEventListener("beforeinstallprompt", onPrompt);
			window.removeEventListener("appinstalled", onInstalled);
		};
	}, []);

	if (!visible) return null;

	const onInstall = async () => {
		if (!deferred) return;
		try {
			deferred.prompt();
			const choice = await deferred.userChoice;
			if (choice && choice.outcome === "dismissed") markDismissed();
		} catch (_e) {
			/* noop */
		}
		setDeferred(null);
		setVisible(false);
	};

	const onClose = () => {
		markDismissed();
		setVisible(false);
	};

	return (
		<Wrap role="dialog" aria-live="polite">
			<Icon src={`${process.env.PUBLIC_URL || ""}/logo192.png`} alt="" />
			<Text>
				<Title>{t("pwa.install.title", "Install Spectre")}</Title>
				<Body>
					{iosHint
						? t(
								"pwa.install.iosHint",
								"Tap the Share icon, then \"Add to Home Screen\" to install."
						  )
						: t("pwa.install.body", "Install the app for quicker access and a full-screen experience.")}
				</Body>
			</Text>
			<Actions>
				{!iosHint && (
					<InstallBtn type="button" onClick={onInstall}>
						{t("pwa.install.cta", "Install")}
					</InstallBtn>
				)}
				<CloseBtn type="button" onClick={onClose} aria-label={t("pwa.install.dismiss", "Dismiss")}>
					×
				</CloseBtn>
			</Actions>
		</Wrap>
	);
}

const Wrap = styled.div`
	position: fixed;
	left: 12px;
	right: 12px;
	bottom: calc(12px + env(safe-area-inset-bottom, 0px));
	z-index: 2000;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 14px;
	background: #ffffff;
	color: #00187e;
	border: 1px solid rgba(0, 24, 126, 0.15);
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	max-width: 520px;
	margin: 0 auto;
	font-family: inherit;
`;

const Icon = styled.img`
	width: 40px;
	height: 40px;
	flex: 0 0 auto;
`;

const Text = styled.div`
	flex: 1 1 auto;
	min-width: 0;
`;

const Title = styled.div`
	font-weight: 600;
	font-size: 14px;
	line-height: 1.2;
`;

const Body = styled.div`
	font-size: 12px;
	line-height: 1.3;
	opacity: 0.8;
	margin-top: 2px;
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 0 0 auto;
`;

const InstallBtn = styled.button`
	background: #00187e;
	color: #ffffff;
	border: 0;
	border-radius: 8px;
	padding: 8px 14px;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	&:hover {
		background: #001a90;
	}
`;

const CloseBtn = styled.button`
	background: transparent;
	color: #00187e;
	border: 0;
	font-size: 22px;
	line-height: 1;
	cursor: pointer;
	padding: 4px 8px;
	&:hover {
		opacity: 0.7;
	}
`;
